import React, { useContext, useEffect, useMemo, useState } from "react";
import AuthManager from "../../../Common/AuthManager";
import Utils from "../../../Common/Utils";
import { AppContext } from "../../../Context/AppContext";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AppSettings from "../../../Settings/AppSettings";
import Table from "Components/DesignSystem/Table/Table";
import Pagination from "Components/DesignSystem/Pagination/Pagination";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";
import Button from "Components/DesignSystem/Button/Button";
import Tag from "Components/DesignSystem/Tag/Tag";
import Typography from "Components/DesignSystem/Typography/Typography";

const formatPDWDate = (dateStr) => {
  if (!dateStr) return '';
  // API returns Unix timestamps in seconds; multiply by 1000 for JS Date (milliseconds)
  const date = new Date(parseFloat(dateStr) * 1000);
  if (isNaN(date.getTime())) return String(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  }) + ' UTC';
};

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' },
  { value: 500, label: '500' },
  { value: 10000, label: 'All' },
];

const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 11-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);

/**
 * The PDW job log — "View Logs" on `/gms/pdw`, and the same table inside the
 * Products list's `LogsPDW` dialog. On the design system since § 126.
 *
 * What the migration replaced, and why each mattered:
 *
 * - **A hand-rolled `<table>` where every BODY cell was a `<th>`**, wrapped in one
 *   `<tbody>` per row, with `key={index}` on the inner `<tr>`. A screen reader
 *   announces a `<th>` as a header for its row or column; four per row means the
 *   table had no data cells at all.
 * - **The page-size control was a native `<select>` labelled
 *   `aria-label="Default select example"`** — copied out of the Bootstrap docs. Its
 *   accessible name told the user nothing about what it does.
 * - **`apiCalled` was a PARITY TOGGLE used as a loading flag**: `setApiCalled(prev =>
 *   !prev)` on fetch start and again on every outcome. Correct only while exactly one
 *   request is ever in flight — click Refresh twice quickly and the flag inverts, so
 *   the skeleton shows when loading has finished and hides while it has not. It is a
 *   plain boolean now.
 * - **Pagination went through the legacy `Paginator`**, which handed the paged slice
 *   back through a callback. This slices locally and drives DS `Pagination` from a
 *   page number, so the current page survives a page-size change instead of being
 *   whatever the callback last pushed into state.
 *
 * The fetch itself, the descending `updated_at` sort, the comma-split status badges
 * and the UTC date formatting are all unchanged.
 */
const PDWTracker = () => {
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  let appData = useContext(AppContext);
  let industryId =
    appData.get_selected_industry() && appData.get_selected_industry().id;

  const [loading, setLoading] = useState(false);
  const [recordPerPage, setRecordPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [filteredList, setFilteredList] = useState([]);

  useEffect(() => {
    load_status_data();
  }, [industryId]);

  async function load_status_data() {
    setFilteredList([]);
    setLoading(true);

    let res = await ServiceWrapper.doGet(
      `${api_base_url}pdw/industry/list`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    let descedingOrder = res?.data?.data?.sort(
      (a, b) => parseFloat(b.updated_at) - parseFloat(a.updated_at)
    );

    if (!res.errorfound && res.data.done) {
      setFilteredList(descedingOrder);
      setPage(1);
    }
    setLoading(false);
  }

  /* Local slicing — the legacy Paginator did this and pushed the result back
     through `onPageChange`, which is why the component held a second copy of the
     rows in state. */
  const singlePageProducts = useMemo(() => {
    const size = Number(recordPerPage);
    const start = (page - 1) * size;
    return (filteredList || []).slice(start, start + size);
  }, [filteredList, page, recordPerPage]);

  const columns = [
    {
      id: 'name',
      header: 'NAME/ID',
      accessor: 'filename',
      render: (_value, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <Typography variant="body-primary" as="span" className="tw-text-ink">
            {row.filename}
          </Typography>
          <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
            {row.id}
          </Typography>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cellType: 'tag',
      render: (_value, row) => (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {Utils.getListFromCommaSeparatedString(row.status).map((item, index) => (
            /* `meta`, not `selected`: a status DESCRIBES the job, nobody picked it.
               Matches the legacy grey chip. */
            <Tag key={`${row.id}-status-${index}`} label={item} variant="meta" />
          ))}
        </div>
      ),
    },
    {
      id: 'created_on',
      header: 'Created On',
      accessor: 'created_on',
      width: '25%',
      cellType: 'meta',
      render: (_value, row) => formatPDWDate(row.created_on),
    },
    {
      id: 'updated_at',
      header: 'Updated at',
      accessor: 'updated_at',
      width: '25%',
      cellType: 'meta',
      render: (_value, row) => formatPDWDate(row.updated_at),
    },
  ];

  const hasRows = filteredList && filteredList.length > 0;

  return (
    <div data-testid="pdw-pdwtracker">
      <div className="tw-flex tw-justify-end tw-mb-2">
        <Button
          variant="tertiary"
          size="sm"
          data-testid="pdw-pdwtracker-refresh-btn"
          aria-label="Refresh job list"
          onClick={() => load_status_data()}
        >
          <RefreshIcon />
        </Button>
      </div>

      {hasRows || loading ? (
        <>
          <Table
            columns={columns}
            data={singlePageProducts}
            rowKey="id"
            striped
            bordered
            stickyHeader
            maxHeight="60vh"
            loading={loading}
            data-testid="pdw-pdwtracker-table"
          />
          <div className="tw-mt-3 tw-flex tw-items-end tw-justify-end tw-gap-4">
            <Dropdown
              label="Rows"
              labelSize="sm"
              size="sm"
              className="tw-w-[92px]"
              options={PAGE_SIZE_OPTIONS}
              value={recordPerPage}
              onChange={(value) => {
                setRecordPerPage(Number(value));
                /* Back to page 1 — page 7 of a 10-per-page list does not exist once
                   the size becomes 500, and the legacy Paginator's `redraw()` had to
                   catch that after the fact. */
                setPage(1);
              }}
              /* Portalled: this renders inside a DS Dialog whose content area is
                 `overflow-y-auto`, which would clip the menu. Dialog treats a
                 `[data-dropdown-portal]` mousedown as inside, so opening the menu
                 does not dismiss the dialog under it. */
              usePortal
              data-testid="pdw-pdwtracker-page-size"
            />
            <Pagination
              page={page}
              pageSize={Number(recordPerPage)}
              totalItems={filteredList?.length ?? 0}
              onPageChange={setPage}
              size="sm"
              data-testid="pdw-pdwtracker-pagination"
            />
          </div>
        </>
      ) : (
        <EmptyState
          title="No data found"
          illustration={<NoDataIllustration />}
          data-testid="pdw-pdwtracker-empty"
        />
      )}
    </div>
  );
};

export default PDWTracker;
